/*
 * list.tsx
 * This dealer list Component is used for representing all the saved dealers in list Form.
 */
// Import necessary modules and components
import { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  Drawer,
  Input,
  InputRef,
  Select,
  Space,
  Table,
  TableColumnsType,
  TableColumnType,
  Tag,
  TablePaginationConfig,
  Tooltip,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  EditOutlined,
  FilterFilled,
  FilterOutlined,
  IdcardOutlined,
  MoreOutlined,
  OrderedListOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import Highlighter from "react-highlight-words";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { UserContext } from "../../../../context";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import { PageHeader } from "../../../../components/page-header/page-header";
import {
  TDropdownOptions,
  TResponseModel,
  TUser,
  TFilterModel,
} from "../../../../types/config";
import {
  eColumnConfigKeys,
  eDEALER_VIEW_TYPE,
  eHTTPStatusCode,
  eORGANIZATION,
  ePrivileges,
  eResultCode,
} from "../../../../utils/enum";
import {
  dateOnlyFormat,
  defaultFilterParams,
  STAKE_HOLDERS,
  UNAUTHORIZEDREQUEST,
} from "../../../../utils/constants";
import DateUtils from "../../../../utils/date";
import Utils from "../../../../utils";
import CustomDrawer from "../../../../components/drawer";
import styles from "./dealer.module.css";
import GenericPopover from "../../../../components/pop-over";
import { TCustomer, TDealer, TDealerMaster } from "../../../../types";
import BulkUploadForm from "../bulk-upload/bulk-upload-form";
import {
  AddEditDealer,
  GetCustomerList,
  GetDealerList,
  GetDealerMasterList,
  GetUploadedADLData,
} from "../../../../utils/api.constant";
import { FilterDropdownProps } from "antd/es/table/interface";
import ColumnManager from "../../../../components/configurable-columns";
import ActionCard from "../../../../components/actionCard/actionCard";
import AuthUtil from "../../../../utils/auth";
import { ColumnConfigManager } from "../../../../utils/column-management";
/**
 * DealerList is a component that is used to render the list of dealers.
 * It fetches the list of dealers from the API and renders it in a table.
 * It also provides the functionality to add a new dealer, edit an existing one and delete a dealer.
 */

export default function DealerListDataTable() {
  // State and hooks initialization
  const navigate = useNavigate();
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState<TDealer[]>([]);
  const [dealerMasterOptions, setDealerMasterOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );
  const [activeDealerMaster, setActiveDealerMaster] = useState<number | null>();
  const [isActiveList, setIsActiveList] = useState<boolean>();
  const [filterParams, setFilterParams] = useState<TFilterModel>({
    ...defaultFilterParams,
  });
  const context: any = useContext(UserContext);
  const [activeUser, setActiveUser] = useState<TUser>(context?.user);
  const [pageSize, setPageSize] = useState(10);
  const [globalsearchText, setGlobalSearchText] = useState("");
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});

  useEffect(() => {
    getCustomerList();
  }, []);

  useEffect(() => {
    setColumns(initialColumns);
  }, [context?.user?.privilegeList]);

  useEffect(() => {
    setActiveUser(context?.user);
    if (
      context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER &&
      context?.user?.orgId
    ) {
      setFilterParams((prevFilter) => ({
        ...prevFilter,
        customerId: context?.user?.orgId,
        customerName: context?.user?.orgName || "",
      }));
    }
  }, [context?.user, activeUser]);

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<TDealer> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Filter by keyword"
          prefix={
            <SearchOutlined
              style={{ color: "rgba(0,0,0,.45)", marginRight: 4 }}
            />
          } // Adds and spaces the search icon
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px", // Ensure spacing for both icon and text
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          {/* RESET FILTERS */}
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            // icon={<SearchOutlined />}
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--bs-primary)",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  useEffect(() => {
    updateColumnFilters(filteredData);
  }, [activeFilters, data]);

  const commonSorter = (a: any, b: any, key: string) => {
    const valueA = a[key] as string;
    const valueB = b[key] as string;
    return valueA?.localeCompare(valueB);
  };

  const initialColumns: TableColumnsType<TDealer> = [
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
      width: 170,
      ...getColumnSearchProps("customerName"),
      filterSearch: true,
      sorter: (a, b) => commonSorter(a, b, "customerName"),
    },
    {
      title: "Service Partner",
      dataIndex: "servicePartnerName",
      key: "servicePartnerName",
      filters: [],
      onFilter: (value, record) =>
        (record.servicePartnerName as string)
          ?.toString()
          .startsWith(value as string),
      width: 150,
      sorter: (a, b) => commonSorter(a, b, "servicePartnerName"),
      filterSearch: true,
    },
    {
      title: "Depot Code",
      dataIndex: "depotCode",
      key: "depotCode",
      ...getColumnSearchProps("depotCode"),
      width: 120,
      sorter: (a, b) => commonSorter(a, b, "depotCode"),
    },
    {
      title: "Dealer Code",
      dataIndex: "dealerCode" as string,
      key: "dealerCode",
      ...getColumnSearchProps("dealerCode"),
      width: 120,
      sorter: (a, b) => commonSorter(a, b, "dealerCode"),
    },
    {
      title: "Dealer Name",
      dataIndex: "dealerName",
      key: "dealerName",
      ...getColumnSearchProps("dealerName"),
      width: 200,
      filterSearch: true,
      sorter: (a, b) => commonSorter(a, b, "dealerName"),
    },
    {
      title: "Dealer Address",
      dataIndex: "address",
      key: "address",
      ...getColumnSearchProps("address"),
      width: 400,
      sorter: (a, b) => commonSorter(a, b, "address"),
    },
    {
      title: "City",
      dataIndex: "districtName",
      key: "districtName",
      filters: [],
      onFilter: (value, record) =>
        (record.districtName as string).toString().startsWith(value as string),
      filterSearch: true,
      width: 100,
      sorter: (a, b) => commonSorter(a, b, "districtName"),
    },
    {
      title: "Pin Code",
      dataIndex: "zipcode",
      key: "zipcode",
      ...getColumnSearchProps("zipcode"),
      width: 100,
      sorter: (a, b) => commonSorter(a, b, "zipcode"),
    },
    {
      title: "State",
      dataIndex: "stateName",
      key: "stateName",
      filters: [],
      onFilter: (value, record) =>
        (record.stateName as string).toString().startsWith(value as string),
      filterSearch: true,
      width: 100,
      sorter: (a, b) => commonSorter(a, b, "stateName"),
    },
    {
      title: "DIV/Region",
      dataIndex: "regionName",
      key: "regionName",
      filters: [],
      sorter: (a, b) =>
        (a.regionName as string).length - (b.regionName as string).length,
      filterSearch: true,
      onFilter: (value, record) =>
        (record.regionName as string).startsWith(value as string),
      width: 100,
    },
    {
      title: "Dealer Contact No.",
      dataIndex: "contactNo",
      key: "contactNo",
      sorter: (a, b) =>
        (a.contactNo as string).length - (b.contactNo as string).length,
      ...getColumnSearchProps("contactNo"),
      width: 150,
    },
    {
      title: "Date of Installation",
      dataIndex: "installationDate",
      key: "installationDate",
      width: 120,
      sorter: (a, b) => commonSorter(a, b, "installationDate"),
      render: (installationDate: string) => {
        return installationDate
          ? DateUtils.format(installationDate, dateOnlyFormat)
          : "-"; // Apply date format here
      },
    },
    {
      title: "Warranty Date",
      dataIndex: "warrantyDate",
      key: "warrantyDate",
      width: 120,
      sorter: (a, b) => commonSorter(a, b, "warrantyDate"),
      render: (warrantyDate: string) => {
        return warrantyDate
          ? DateUtils.format(warrantyDate, dateOnlyFormat)
          : "-"; // Apply date format here
      },
    },
    {
      title: "Machine Type",
      dataIndex: "machineTypeName",
      key: "machineTypeName",
      filters: [],
      sorter: (a, b) =>
        (a.machineTypeName as string).length -
        (b.machineTypeName as string).length,
      filterSearch: true,
      onFilter: (value, record) =>
        (record.machineTypeName as string).startsWith(value as string),
      width: 100,
    },
    {
      title: "Model",
      dataIndex: "productName",
      key: "productName",
      filters: [],
      sorter: (a, b) =>
        (a.productName as string).length - (b.productName as string).length,
      filterSearch: true,
      onFilter: (value, record) =>
        (record.productName as string).startsWith(value as string),
      width: 100,
    },
    {
      title: "Machine Serial No.",
      dataIndex: "machineSerialNo",
      key: "machineSerialNo",
      sorter: (a, b) =>
        (a.machineSerialNo as string).length -
        (b.machineSerialNo as string).length,
      ...getColumnSearchProps("machineSerialNo"),
      width: 160,
    },
    {
      title: "Gyro Yes/NO",
      dataIndex: "gyro",
      key: "gyro",
      filters: [
        { text: "Yes", value: 1 },
        { text: "No", value: 0 },
      ],
      filterSearch: true,
      onFilter: (value, record) => record.gyro === (value as any),
      width: 100,
      render: (gyro: boolean) => (
        <Tag color={gyro ? "var(--bs-primary)" : "#f50"}>
          {gyro ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Gyro Type",
      dataIndex: "gyroType",
      key: "gyroType",
      filters: [],
      sorter: (a, b) =>
        (a.gyroType as string).length - (b.gyroType as string).length,
      filterSearch: true,
      onFilter: (value, record) =>
        (record.gyroType as string).startsWith(value as string),
      width: 100,
    },
    {
      title: "UPS",
      dataIndex: "ups",
      key: "ups",
      filters: [
        { text: "Yes", value: 1 },
        { text: "No", value: 0 },
      ],
      filterSearch: true,
      onFilter: (value, record) => record.ups === (value as any),
      width: 100,
      render: (ups: boolean) => (
        <Tag color={ups ? "var(--bs-primary)" : "#f50"}>
          {ups ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Galileo",
      dataIndex: "galileo",
      key: "galileo",
      filters: [
        { text: "Yes", value: 1 },
        { text: "No", value: 0 },
      ],
      filterSearch: true,
      onFilter: (value, record) => record.galileo === (value as any),
      width: 100,
      render: (galileo: boolean) => (
        <Tag color={galileo ? "var(--bs-primary)" : "#f50"}>
          {galileo ? "Yes" : "No"}
        </Tag>
      ),
    },
    {
      title: "Location Type",
      dataIndex: "locationTypeName",
      key: "locationTypeName",
      filters: [],
      sorter: (a, b) =>
        (a.locationTypeName as string).length -
        (b.locationTypeName as string).length,
      filterSearch: true,
      onFilter: (value, record) =>
        (record.locationTypeName as string)?.startsWith(value as string),
      width: 100,
    },
    {
      title: "SLA",
      dataIndex: "sla",
      key: "sla",
      filters: [
        { text: "24", value: "24" },
        { text: "48", value: "48" },
        { text: "72", value: "72" },
      ],
      onFilter: (value, record) =>
        record?.sla.toString()?.startsWith(value as string),
      sorter: (a, b) => (a.sla as number) - (b.sla as number),
      width: 80,
    },
    {
      title: "AMC From",
      dataIndex: "amcFromDate",
      key: "amcFromDate",
      width: 130,
      sorter: (a, b) => commonSorter(a, b, "amcFromDate"),
      render: (amcFromDate: string) => {
        return amcFromDate
          ? DateUtils.format(amcFromDate, dateOnlyFormat)
          : "-"; // Apply date format here
      },
    },
    {
      title: "AMC To",
      dataIndex: "amcToDate",
      key: "amcToDate",
      width: 130,
      sorter: (a, b) => commonSorter(a, b, "amcToDate"),
      render: (amcToDate: string) => {
        return amcToDate ? DateUtils.format(amcToDate, dateOnlyFormat) : "-"; // Apply date format here
      },
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      filters: [
        { text: "Active", value: 1 },
        { text: "In Active", value: 0 },
      ],
      onFilter: (value, record) => record.isActive === (value as any),
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? "var(--bs-primary)" : "#f50"}>
          {isActive ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Last PM",
      dataIndex: "lastPM",
      key: "lastPM",
      sorter: (a, b) =>
        (a.lastPM as string).length - (b.lastPM as string).length,
      filterSearch: true,
      render: (lastPM: string) => {
        return lastPM ? DateUtils.format(lastPM, dateOnlyFormat) : "-"; // Apply date format here
      },
      width: 130,
    },

    // {
    //   title: "Action",
    //   key: "actions",
    //   align: "center",
    //   width: "4%",
    //   render: (record: TCustomer) => (
    //     <>
    //       <GenericPopover
    //         placement="left"
    //         content={
    //           <div className="standardActionBox">
    //             {Utils.isUserHasAccess(
    //               context?.user?.privilegeList,
    //               ePrivileges.EDITDEALERS
    //             ) && isActiveList ? (
    //               <ActionCard
    //                 text="Edit"
    //                 onClick={() => onAddEdit(record.id as number)}
    //               >
    //                 <EditOutlined />
    //               </ActionCard>
    //             ) : null}
    //           </div>
    //         }
    //         icon={
    //           <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
    //         }
    //       />
    //     </>
    //   ),
    // },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const [draftColumns, setDraftColumns] = useState(columns);
  const { showToast, onShowToast } = useToast();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null); // State to keep track of selected user for modal
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);
  type DataIndex = keyof TDealer;

  useEffect(() => {
    const updatedColumns = initialColumns?.filter(
      (col) => col.key !== "action"
    );
    if (isActiveList) {
      updatedColumns.push({
        title: "Action",
        key: "actions",
        align: "center",
        width: 100,
        render: (record: TCustomer) => (
          <>
            <GenericPopover
              placement="left"
              content={
                <div className="standardActionBox">
                  {Utils.isUserHasAccess(
                    context?.user?.privilegeList,
                    ePrivileges.EDITDEALERS
                  ) && isActiveList ? (
                    <ActionCard
                      text="Edit"
                      onClick={() => onAddEdit(record.id as number)}
                    >
                      <EditOutlined />
                    </ActionCard>
                  ) : null}
                </div>
              }
              icon={
                <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
              }
            />
          </>
        ),
      });
    }
    setColumns(updatedColumns);
    setDraftColumns(updatedColumns);
  }, [isActiveList]);

  /**
   * Handles the OK button click on the modal, logs the user details
   * and closes the modal.
   * @param {TDealer} detalerDetails - The dealer details object.
   */
  const handleOk = (userDetails: TDealer) => {
    updateDealerDetails(userDetails);
    setSelectedUserId(null);
  };

  const handleCancel = () => {
    setSelectedUserId(null);
  };

  // will fetchData when filterParams changes
  useEffect(() => {
    fetchRecordsInChunks();
  }, [activeDealerMaster]);

  useEffect(() => {
    const newUrl = new URL(window.location.href);
    const customerId = newUrl.searchParams.get("customerId");
    const customerName = newUrl.searchParams.get("customerName");

    if (customerId) {
      setFilterParams((prev) => ({
        ...prev,
        customerId: parseInt(customerId, 10),
        customerName: customerName as string,
      }));
    } else if (customerOptions.length > 0 && !filterParams.customerId) {
      const firstOption = customerOptions[0];
      setFilterParams((prev) => ({
        ...prev,
        customerId: firstOption.value as number,
        customerName: firstOption.label,
      }));
    }
  }, [customerOptions]); // Ensure this runs only when customerOptions changes

  useEffect(() => {
    if (filterParams.customerId) {
      setData([]);
      getDealerMasterList();
    }
  }, [filterParams.customerId]);

  useEffect(() => {
    setData([]);
    getDealerMasterList();
  }, [filterParams.customerId]);

  const fetchRecordsInChunks = async () => {
    const chunkSize = context?.chunkSize; // Fixed size for the first chunk
    setData([]);
    handleResetAll();
    setIsLoading(true);

    try {
      // Step 1: Fetch the total number of records
      const totalRecords = await fetchData(1, 1, true);
      setFilterParams({
        ...filterParams,
        totalRows: (totalRecords as number) || 0,
      });

      // Step 2: Fetch the first chunk
      const resp = await fetchData(1, chunkSize);
      const validFirstChunk = Array.isArray((resp as TResponseModel)?.data)
        ? (resp as TResponseModel)?.data
        : []; // Ensure valid data
      setData(validFirstChunk); // Set data to display the first chunk immediately
      updateColumnFilters((resp as TResponseModel).data);
      // Step 3: Determine the remaining records
      const remainingRecords =
        (totalRecords as number) > chunkSize
          ? (totalRecords as number) - chunkSize
          : 0;

      // Step 4: Fetch the remaining records
      if (remainingRecords > 0) {
        const response = await fetchData(2, remainingRecords);
        const validSecondChunk = Array.isArray(
          (response as TResponseModel)?.data
        )
          ? (response as TResponseModel)?.data
          : []; // Ensure valid data

        // Combine the data from both chunks
        updateColumnFilters([...validFirstChunk, ...validSecondChunk]);
        setData((prevData) => [...prevData, ...validSecondChunk]);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateColumnFilters = (data: TDealer[]) => {
    const servicePartner = Array.from(
      new Set(data?.map((item: TDealer) => item.servicePartnerName))
    ).map((name) => ({ text: name, value: name }));

    const cityNames = Array.from(
      new Set(data?.map((item: TDealer) => item.districtName))
    ).map((name) => ({ text: name, value: name }));

    const stateName = Array.from(
      new Set(data?.map((item: TDealer) => item.stateName))
    ).map((name) => ({ text: name, value: name }));

    const regions = Array.from(
      new Set(data?.map((item: TDealer) => item.regionName))
    ).map((name) => ({ text: name, value: name }));

    const machineTypes = Array.from(
      new Set(data?.map((item: TDealer) => item.machineTypeName))
    ).map((name) => ({ text: name, value: name }));

    const models = Array.from(
      new Set(data?.map((item: TDealer) => item.productName))
    ).map((name) => ({ text: name, value: name }));

    const locationType = Array.from(
      new Set(data?.map((item: TDealer) => item.locationTypeName))
    ).map((name) => ({ text: name, value: name }));

    setColumns(
      (prevColumns) =>
        prevColumns.map((col) => {
          if (col.key === "servicePartnerName") {
            return { ...col, filters: servicePartner };
          }
          if (col.key === "districtName") {
            return { ...col, filters: cityNames };
          }
          if (col.key === "stateName") {
            return { ...col, filters: stateName };
          }
          if (col.key === "regionName") {
            return { ...col, filters: regions };
          }
          if (col.key === "machineTypeName") {
            return { ...col, filters: machineTypes };
          }
          if (col.key === "productName") {
            return { ...col, filters: models };
          }
          if (col.key === "locationTypeName") {
            return { ...col, filters: locationType };
          }
          return col;
        }) as any
    );
  };

  const filteredData = data.filter((item) => {
    const searchMatch =
      globalsearchText === "" ||
      Object.values(item).some((val) =>
        val?.toString().toLowerCase().includes(globalsearchText.toLowerCase())
      );

    const servicePartnerNameMatch =
      !activeFilters.servicePartnerName ||
      activeFilters.servicePartnerName.includes(item.servicePartnerName);

    const districtNameMatch =
      !activeFilters.districtName ||
      activeFilters.districtName.includes(item.districtName); 
    const stateNameMatch = 
      !activeFilters.stateName ||
      activeFilters.stateName.includes(item.stateName);
    const regionNameMatch =
      !activeFilters.regionName ||
      activeFilters.regionName.includes(item.regionName);
    const machineTypeNameMatch =
      !activeFilters.machineTypeName ||
      activeFilters.machineTypeName.includes(item.machineTypeName);
    const productNameMatch =
      !activeFilters.productName ||
      activeFilters.productName.includes(item.productName);
    const locationTypeNameMatch =
      !activeFilters.locationTypeName ||
      activeFilters.locationTypeName.includes(item.locationTypeName);

    return ( 
      searchMatch &&
      servicePartnerNameMatch &&
      districtNameMatch &&
      stateNameMatch &&
      regionNameMatch &&
      machineTypeNameMatch &&
      productNameMatch &&
      locationTypeNameMatch
    );
  });

  /**
   * Fetches the list of dealers from the API.
   * Sets the isLoading state to true, sends a request with the current filterParams, and sets the data and filterParams state with the response.
   * If the response is not successful, shows an error toast.
   * Finally, sets isLoading to false.
   */
  const fetchData = async (
    offset: number,
    chunkSize: number,
    needTotalRows: boolean = false
  ) => {
    if (filterParams.customerId && activeDealerMaster) {
      try {
        let { customerId, pageSize, currentPage, searchText, ...restFilters } =
          filterParams;
        const requestPayload = {
          data: {
            pageSize: chunkSize,
            currentPage: offset,
            searchText: "",
            ...restFilters,
            customerId,
            dealerMasterId: activeDealerMaster,
            dealerViewId: eDEALER_VIEW_TYPE.ANYDEALERLIST,
            isChunkAPICall: true,
            isTotalCountRequest: needTotalRows ? 1 : 0,
          },
        };

        const response: TResponseModel = await post(
          GetDealerList,
          requestPayload
        );
        const { dataResponse, data, filterModel } = response;
        if (dataResponse.returnCode === eResultCode.SUCCESS) {
          // setIsLoading(false);
          return needTotalRows ? filterModel?.totalRows : response;
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: dataResponse.description,
          });
          // setIsLoading(false);
        }
      } catch (error) {
        console.error("Error:", error);
        // setIsLoading(false);
      } finally {
        // setIsLoading(false);
      }
    }
    // setIsLoading(false);
  };

  const handleResetAll = () => {
    setActiveFilters({});
    setSearchText("");
    setColumns((prevColumns) =>
      prevColumns.map((col) => ({
        ...col,
        filteredValue: null,
        sortOrder: null,
      }))
    );
  };

  const getCustomerList = async () => {
    try {
      const payload = {
        data: {
          pageSize: -1,
        },
      };

      const response: TResponseModel = await post(GetCustomerList, payload);
      if (response) {
        const { dataResponse, data } = response;
        const { returnCode, description } = dataResponse;
        if (returnCode === eResultCode.SUCCESS) {
          const mappedOption = Utils.mapDropdownOptions(data);
          setCustomerOptions(mappedOption);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: description,
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const getDealerMasterList = async () => {
    if (filterParams.customerId) {
      try {
        setActiveDealerMaster(null);
        const requestPayload = {
          data: {
            pageSize: -1,
            customerId: filterParams.customerId,
            currentPage: 1,
          },
        };

        const response: TResponseModel = await post(
          GetDealerMasterList,
          requestPayload
        );
        const { dataResponse, data } = response;
        if (dataResponse.returnCode === eResultCode.SUCCESS) {
          setDealerMasterOptions(
            data.map((item: TDealerMaster) => {
              // Extracting and formatting the month and year from createdOn
              const createdDate = new Date(item?.uploadMonthDate as any);
              const monthYear = createdDate.toLocaleString("default", {
                month: "long",
                year: "numeric",
              });

              // Building the label
              const status = item.isActive === 1 ? "Current" : "";
              const label = status ? `${monthYear} - ${status}` : monthYear;
              if (item.isActive) {
                setIsActiveList(true);
                setActiveDealerMaster(item.id);
              }
              return {
                value: item.id,
                label: label,
                data: item,
              };
            })
          );
          // Utils.mapDropdownOptions()
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: dataResponse.description,
          });
        }
      } catch (error) {
        console.error("Error:", error);
      }
    }
  };

  /**
   * Updates the dealer with the given values.
   * Sends a request to the API with the given values, and if the response is successful, shows a success toast and fetches the data again.
   * If the response is not successful, shows an error toast with the description from the response.
   * @param {TDealer} values - the dealer values to update.
   */
  const updateDealerDetails = async (values: TDealer) => {
    try {
      const { isActive, ...restValues } = values;
      const payload = {
        data: {
          ...restValues,
          isActive: isActive ? 1 : 0,
        },
      };
      const response = await post(AddEditDealer, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        fetchRecordsInChunks();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      console.log("Finally");
    }
  };

  const onAddEdit = (id: number | null) => {
    const formURL = id
      ? `/dashboard/dealers/form?id=${id}`
      : "/dashboard/dealers/form";
    navigate(formURL);
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  // Helper to generate unique filter options

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, any>,
    sorter: any
  ) => {
    setActiveFilters(filters);
    setColumns((prevColumns) =>
      prevColumns.map((col) => {
        if ("dataIndex" in col) {
          return {
            ...col,
            filteredValue: filters[col.dataIndex as string] || null,
            sortOrder: col.dataIndex === sorter.field ? sorter.order : null,
          };
        }
        return col;
      })
    );
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    setDraftColumns((prevDraftColumns) => {
      const reorderedColumns = Array.from(prevDraftColumns);
      const [removed] = reorderedColumns.splice(result.source.index, 1);
      reorderedColumns.splice(result.destination.index, 0, removed);
      return reorderedColumns;
    });
  };

  const toggleColumn = (key: string) => {
    setDraftColumns((prevDraftColumns) =>
      prevDraftColumns.map((col) =>
        col.key === key ? { ...col, hidden: !col.hidden } : col
      )
    );
  };

  const handleResetColumns = () => {
    setDraftColumns(initialColumns); // Reset staging state to initial state
  };

  const handleSaveColumns = () => {
    setColumns(draftColumns); // Apply changes to the final state
    setDrawerVisible(false); // Close the drawer
  };

  const visibleColumns = columns.filter((col) => !col.hidden);

  // To download the ADL from server
  const handleADLDownload = async () => {
    try {
      // Start loading
      setIsLoading(true);

      // Fetch the XLSX file
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${GetUploadedADLData}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Ensure JSON payload
            ...AuthUtil.getToken(),
          },
          body: JSON.stringify({
            data: {
              dealerMasterId: activeDealerMaster,
            },
          }), // Convert body to JSON string
        }
      );

      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        // Extract content-disposition header to get the file name
        const contentDisposition = response.headers.get("Content-Disposition");
        let fileName = `${filterParams.customerName}_ADL.xlsx`; // Default file name
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch?.[1]) {
            fileName = fileNameMatch[1];
          }
        }

        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        // Set the file name for the download
        link.download = fileName; // Use file name from response header or default

        // Append the link to the document and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up by removing the link and revoking the URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: "File downloaded successfully!",
        });
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content:
            "Error while downloading sample file, please try again later.",
        });
      }
    } catch (error) {
      // Handle any exceptions
      console.error("Download error:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error downloading file",
      });
    } finally {
      // Stop loading
      setIsLoading(false);
    }
  };

  // Return JSX structure for User Component
  return (
    <div className="pageBody">
      <PageHeader
        icon={<IdcardOutlined />}
        title={`${filterParams?.totalRows} Total Dealers`}
        size="large"
        className={styles.pageHeaderStyles}
      >
        <div className={styles.headerParentWrap}>
          <div style={{ display: "flex" }}>
            <Select
              disabled={
                activeUser?.stakeHolderName === STAKE_HOLDERS.CUSTOMER ||
                isLoading
                  ? true
                  : false
              }
              variant="borderless"
              placeholder="Select customer"
              style={{ width: "15vw" }}
              onChange={(value, data) => {
                console.log({ data });
                const newurl = new URL(window.location.href);
                newurl.searchParams.set("customerId", value.toString());
                newurl.searchParams.set(
                  "customerName",
                  (data as any)?.label.toString()
                );
                window.history.pushState({}, "", newurl.toString());
                setFilterParams({
                  ...filterParams,
                  customerId: value,
                  customerName: (data as any)?.label,
                });
              }}
              value={filterParams.customerId || null}
              filterOption={(input, option) =>
                option?.label
                  ? option.label.toLowerCase().includes(input.toLowerCase())
                  : false
              }
              showSearch
              options={customerOptions}
            />
            <Select
              disabled={isLoading}
              variant="borderless"
              placeholder="Select timeline"
              style={{ width: "15vw" }}
              onChange={(value, data) => {
                setIsActiveList((data as any)?.data?.isActive);
                setActiveDealerMaster(value);
                setFilterParams({
                  ...filterParams,
                  currentPage: 1,
                  pageSize: 10,
                  searchText: "",
                  stateId: 0,
                  districtId: 0,
                  orderBy: "",
                  orderType: "",
                  dealerId: 0,
                });
              }}
              value={activeDealerMaster}
              filterOption={(input, option) =>
                option?.label
                  ? option.label.toLowerCase().includes(input.toLowerCase())
                  : false
              }
              showSearch
              options={dealerMasterOptions}
            />
          </div>
          <div className={styles.headerRightSection}>
            {/* <Button onClick={handleResetAll}>Reset</Button> */}
            {Utils.isUserHasAccess(
              context.user?.privilegeList,
              ePrivileges.BULKUPLOADDEALERS
            ) ? (
              <CustomDrawer
                trigger={
                  <Button disabled={isLoading} type="primary">
                    Import
                  </Button>
                }
                component={BulkUploadForm} // Passing DealerForm component
                componentProps={{
                  orgType: eORGANIZATION.DEALER,
                  onRefreshList: () => {
                    /**
                     * Resets the filter parameters to its default values and refetches the data.
                     * Used as a callback when the user is done with importing a file and wants to go back to the list view.
                     */
                    setFilterParams({
                      ...defaultFilterParams,
                      customerId: filterParams.customerId,
                    });
                    getDealerMasterList();
                  },
                }}
                title="Bulk Upload Form"
                size="default"
              />
            ) : null}
            {Utils.isUserHasAccess(
              context.user?.privilegeList,
              ePrivileges.ADDDEALERS
            ) ? (
              <Button
                disabled={isLoading || !isActiveList}
                type="primary"
                onClick={() => onAddEdit(null)}
              >
                Add
              </Button>
            ) : null}
            <Input
              placeholder="Filter by keywords"
              value={globalsearchText}
              onChange={(e) => setGlobalSearchText(e.target.value)}
              style={{ width: 200, borderColor: "var(--bs-primary-light)" }}
              prefix={<SearchOutlined />}
            />
            {/* Dynamic download of dealer list */}
            <Tooltip title="Download Excel">
              <DownloadOutlined
                style={{ cursor: "pointer" }}
                onClick={async () => {
                  await handleADLDownload();
                }}
              />
            </Tooltip>
            <Tooltip title="Reset Filters">
              <FilterOutlined onClick={handleResetAll} />
            </Tooltip>
            <Tooltip title="Configure Columns">
              <OrderedListOutlined onClick={() => setDrawerVisible(true)} />
            </Tooltip>
          </div>
        </div>
      </PageHeader>
      <div className="tableWrap">
        <Table<TDealer>
          // key={tableKey}
          scroll={{
            x: "max-content", // Horizontal scrolling
            y: "calc(100vh - 280px)", // Adjust height dynamically
          }}
          columns={visibleColumns}
          loading={isLoading}
          dataSource={filteredData}
          onChange={handleTableChange}
          pagination={{
            // ...pagination,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (total, range) =>
              `Showing ${range[0]}-${range[1]} of ${total} total entries`,
            showSizeChanger: true,
            onShowSizeChange: (_, size) => setPageSize(size),
            // showQuickJumper: true,
            position: ["bottomLeft"],
          }}
          sticky
          // size="small"
        />

        <Drawer
          title="Customize Columns"
          placement="right"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          style={{ transition: "transform 0.3s ease-in-out" }}
        >
          <ColumnManager
            columns={draftColumns as any}
            initialColumns={initialColumns as any}
            onDragEnd={handleDragEnd}
            onToggleColumn={toggleColumn}
            onResetColumns={handleResetColumns}
            onSave={handleSaveColumns}
            onCancel={() => setDrawerVisible(false)}
          />
        </Drawer>
      </div>
    </div>
  );
}
